import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FieldArray } from 'formik';
import { array, object, string } from 'yup';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { useCreateClientInvitationMutation, CreateClientInvitationInput } from 'api';
import { useNotify } from 'hooks';

import { ValidatedTextField, PlusButton, Loaders } from './';

interface IProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles(theme => ({
  title: {
    justifyContent: 'flex-end',
    marginBottom: 0,
    borderBottom: 0,
  },
  intro: {
    maxWidth: '75%',
    margin: `0 auto ${theme.spacing(2)}px`,
  },
  loader: {
    margin: `14px 0`,
  },
  submit: {
    padding: `${theme.spacing(2)}px ${theme.spacing(8)}px`,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  deleteIcon: {
    transform: 'rotate(45deg)',
  },
  completedIcon: {
    fill: theme.palette.primary.main,
    fontSize: 80,
  },
}));

const initialValues: CreateClientInvitationInput = {
  email_addresses: [''],
};

const InviteDialog: FC<IProps> = ({ open, onClose }) => {
  const [completed, setCompleted] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles();
  const notify = useNotify();

  const schema = object().shape({
    email_addresses: array()
      .of(string().email(t('admin:emailInvalid')).required(t('admin:emailRequired')))
      .required()
      .min(1),
  });

  const { mutate, isLoading } = useCreateClientInvitationMutation({
    onError: notify.mutationError,
    onSuccess: () => {
      setCompleted(true);
    },
  });

  function handleSubmit(values: CreateClientInvitationInput) {
    return mutate({ data: values });
  }

  function renderTitle() {
    return (
      <DialogTitle className={classes.title} disableTypography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
    );
  }

  function renderEmail(email: string, index: number, onRemove: () => void) {
    let endAdornment = null;

    if (index > 0) {
      endAdornment = (
        <InputAdornment position="end">
          {' '}
          <IconButton onClick={onRemove}>
            <AddCircleIcon className={classes.deleteIcon} />
          </IconButton>
        </InputAdornment>
      );
    }

    return (
      <ValidatedTextField
        key={index}
        name={`email_addresses[${index}]`}
        i18nKey="user:email"
        InputProps={{ endAdornment }}
      />
    );
  }

  function renderFields(values: CreateClientInvitationInput) {
    return (
      <FieldArray name="email_addresses">
        {arrayHelpers => (
          <Fragment>
            {values.email_addresses.map((email, index) =>
              renderEmail(email, index, () => {
                arrayHelpers.remove(index);
              })
            )}
            <PlusButton i18nKey="invites:addMore" onClick={() => arrayHelpers.push('')} />
          </Fragment>
        )}
      </FieldArray>
    );
  }

  function renderSubmit(isValid: boolean) {
    return (
      <Grid container justify="center">
        <Grid item>
          <Button
            disabled={!isValid}
            className={classes.submit}
            size="large"
            type="submit"
            color="primary"
            variant="contained"
          >
            {isLoading ? <Loaders.Standard height={26} /> : t('invites:send')}
          </Button>
        </Grid>
      </Grid>
    );
  }

  function renderForm() {
    return (
      <DialogContent>
        <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={schema}>
          {({ isValid, values }) => (
            <Form>
              <Typography paragraph variant="subtitle2" align="center" className={classes.intro}>
                {t('invites:intro')}
              </Typography>
              {renderFields(values)}
              {renderSubmit(isValid)}
            </Form>
          )}
        </Formik>
      </DialogContent>
    );
  }

  function renderCompleted() {
    return (
      <DialogContent>
        <Grid container alignItems="center" spacing={3} direction="column">
          <Grid item>
            <CheckCircleIcon className={classes.completedIcon} />
          </Grid>
          <Grid item>
            <Typography paragraph variant="subtitle2" align="center" className={classes.intro}>
              {t('invites:sent')}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              className={classes.submit}
              onClick={() => setCompleted(false)}
              size="large"
              color="primary"
              variant="contained"
            >
              {t('invites:sendMore')}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    );
  }

  return (
    <Dialog open={open} onBackdropClick={onClose}>
      {renderTitle()}
      {completed ? renderCompleted() : renderForm()}
    </Dialog>
  );
};

export default InviteDialog;
